import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Truck, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import ListFilterBar from './components/ListFilterBar.js';
import { isAdminUser, isOpsManager } from './accessControl.js';
import StatusBadge from './components/StatusBadge.js';
import { formatVehicleNumber, normalizeVehicleNumber } from './utils/vehicleNumber.js';

const InTransit = ({ db, user }) => {
  const navigate = useNavigate();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "dispatches"),
      where("dispatchStatus", "==", "dispatched"),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const dispatchList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setDispatches(dispatchList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching In Transit list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(dispatches.map((dispatch) => dispatch.warehouse).filter(Boolean)))], [dispatches]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(dispatches.map((dispatch) => dispatch.dispatchStatus).filter(Boolean)))], [dispatches]);
  const typeOptions = useMemo(() => ['All', ...Array.from(new Set(dispatches.map((dispatch) => dispatch.typeOfMOC).filter(Boolean)))], [dispatches]);
  const filterFields = useMemo(() => ([
    { key: 'searchTerm', type: 'search', label: 'Customer Search', placeholder: 'Search by Customer, MOC, DC, or Vehicle No...', minWidth: '320px' },
    { key: 'filterType', type: 'select', label: 'MOC Type', options: typeOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Types' : option })) },
    { key: 'filterStatus', type: 'select', label: 'Status', options: statusOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Statuses' : option })) },
    { key: 'filterWarehouse', type: 'select', label: 'Warehouse', options: warehouseOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Warehouses' : option })) }
  ]), [statusOptions, typeOptions, warehouseOptions]);

  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') setSearchTerm(value);
    if (key === 'filterType') setFilterType(value);
    if (key === 'filterStatus') setFilterStatus(value);
    if (key === 'filterWarehouse') setFilterWarehouse(value);
  };

  const filteredDispatches = useMemo(() => {
    const normalizedSearch = normalizeVehicleNumber(searchTerm);

    return dispatches.filter((d) => {
      if (d.dispatchStatus === 'delivered') return false;
      const matchesSearch = (
        d.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.challanNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        normalizeVehicleNumber(d.vehicleNumber).includes(normalizedSearch)
      );
      const matchesWarehouse = filterWarehouse === 'All' || d.warehouse === filterWarehouse;
      const matchesStatus = filterStatus === 'All' || d.dispatchStatus === filterStatus;
      const matchesType = filterType === 'All' || d.typeOfMOC === filterType;
      return matchesSearch && matchesWarehouse && matchesStatus && matchesType;
    });
  }, [dispatches, searchTerm, filterWarehouse, filterStatus, filterType]);

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getActionConfig = (dispatch) => {
    if (isAdminUser(user)) {
      return {
        label: 'Preview',
        style: previewBtnStyle,
        onClick: () => navigate(`/ops/in-transit/${dispatch.id}`)
      };
    }

    const isOpsOrOpsHead = user?.role === 'ops' || isOpsManager(user);

    if (isOpsOrOpsHead && dispatch.dispatchStatus === 'dispatched') {
      return {
        label: 'Confirm Delivery',
        style: confirmDeliveryBtnStyle,
        onClick: () => navigate(`/ops/in-transit/${dispatch.id}`, { state: { mode: 'deliver' } })
      };
    }

    return {
      label: 'Preview',
      style: previewBtnStyle,
      onClick: () => navigate(`/ops/in-transit/${dispatch.id}`)
    };
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>In Transit</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Track material currently moving to customer sites.</p>
        </div>
      </div>

      <ListFilterBar
        fields={filterFields}
        values={{ searchTerm, filterType, filterStatus, filterWarehouse }}
        onFieldChange={handleFilterChange}
      />

      {dispatches.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Truck size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No vehicles in transit!</h3>
          <p style={{ color: '#64748b' }}>Dispatched trucks will appear here.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Dispatch Time</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Customer / Destination</th>
                <th style={thStyle}>DC No.</th>
                <th style={thStyle}>Vehicle No.</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDispatches.map((d) => {
                const actionConfig = getActionConfig(d);

                return (
                  <tr
                    key={d.id}
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                    onClick={() => navigate(`/ops/in-transit/${d.id}`)}
                    className="hover-highlight"
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 'bold' }}>{d.vehicleOutTime || '---'}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{formatDate(d.updatedAt?.toDate ? d.updatedAt.toDate() : d.updatedAt)}</div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{d.customerName}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={12} /> {d.shipToAddress?.substring(0, 40)}...
                      </div>
                    </td>
                    <td style={tdStyle}><strong>{d.challanNo}</strong></td>
                    <td style={tdStyle}>{formatVehicleNumber(d.vehicleNumber) || '---'}</td>
                    <td style={tdStyle}>
                      <StatusBadge status="dispatched" iconSize={12} />
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          actionConfig.onClick();
                        }}
                        style={actionConfig.style}
                      >
                        {actionConfig.label}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const previewBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer' };
const confirmDeliveryBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };

export default InTransit;
